/**
 * A bottom sheet you can drag.
 *
 * The phone version of a dialog. A modal box floating in the middle of a
 * screen is a desktop shape; on a phone the thing you opened should come up
 * from the bottom, sit under your thumb, and go away when you push it back
 * down - which is what the grab bar at the top is for.
 *
 * Dragging follows the finger with no transition, then either snaps back or
 * carries on out depending on how far and how fast it went. A sheet that
 * animates to a fixed position regardless of the throw feels stuck to the
 * screen.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';

export function Sheet({ open, onClose, children, title, full }: {
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  title?: string;
  /** Starts at full height rather than around two thirds. */
  full?: boolean;
}) {
  const panel = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState(0);
  const from = useRef<{ y: number; at: number } | null>(null);

  /* The page behind must not scroll while a sheet is over it. */
  useEffect(() => {
    if (!open) return;
    const had = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = had; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => { if (!open) setDrag(0); }, [open]);

  if (!open) return null;

  function down(e: React.PointerEvent) {
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    from.current = { y: e.clientY, at: Date.now() };
  }

  function move(e: React.PointerEvent) {
    if (!from.current) return;
    /* Downward only. Dragging up should not stretch it off the top. */
    setDrag(Math.max(0, e.clientY - from.current.y));
  }

  function up(e: React.PointerEvent) {
    const started = from.current;
    from.current = null;
    if (!started) return;

    const travelled = e.clientY - started.y;
    const speed = travelled / Math.max(1, Date.now() - started.at);
    const height = panel.current?.getBoundingClientRect().height ?? 400;

    /* A short flick closes it as surely as a long slow pull. */
    if (travelled > height * 0.3 || speed > 0.6) onClose();
    else setDrag(0);
  }

  return createPortal(
    <div className="fixed inset-0 z-[300] flex flex-col justify-end">
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/60 backdrop-blur-[2px] animate-[fade-in_160ms_ease-out]"
        style={{ opacity: Math.max(0, 1 - drag / 400) }}
      />

      <div
        ref={panel}
        className={cn(
          'relative flex max-h-[92vh] flex-col overflow-hidden rounded-t-[calc(18px*var(--radius-mul,1))] border-t border-line bg-surface-1',
          'pb-[env(safe-area-inset-bottom,0px)] shadow-[0_-18px_50px_rgba(0,0,0,.5)]',
          full ? 'h-[92vh]' : 'min-h-[40vh]',
          !drag && 'animate-[sheet-up_220ms_var(--ease-out-soft)]'
        )}
        style={{
          transform: 'translateY(' + drag + 'px)',
          transition: from.current ? 'none' : 'transform 200ms var(--ease-out-soft)'
        }}
      >
        {/* The handle. Deliberately a generous target - a 4px line is only a
            grab bar if you can actually hit it. */}
        <div
          onPointerDown={down}
          onPointerMove={move}
          onPointerUp={up}
          onPointerCancel={up}
          className="flex flex-none cursor-grab touch-none flex-col items-center pt-2.5 pb-1 active:cursor-grabbing"
        >
          <span aria-hidden className="h-1 w-10 rounded-full bg-surface-4" />
          {title ? (
            <span className="mt-2 text-[13px] font-bold">{title}</span>
          ) : null}
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
